import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import { formatLotNumber } from '../../versa/misc.js';
import filter, { resolveFilterLabelsToIds, translate } from '../../versa/filter.js';
import grid from '../../versa/grid.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import { notify, ask } from '../../versa/msgbox.js';
import { splitIdsByCompany, resolvePrinterDeviceId } from '../../versa/misc.js';
import pdf from '../../versa/pdf.js';
import view from '../views/stocks.js';
import form from '../forms/stocks.js';
import move_stock_form from '../forms/stocks-move-stock.js';
import block_stock_form from '../forms/stocks-block-stock.js';
import select_printer_form from '../forms/stocks-select-printer.js';
import report_modal_form from '../forms/stocks-report-modal.js';
import export_csv_form from '../forms/stocks-export-csv.js';
import print_durability_form from '../forms/stocks-print-durability.js';
import sku_no_output_form from '../forms/sku-no-output-report.js';
import output_purchase_form from '../forms/output-purchase-report.js';
import { manualMoveReservedError, formatBlockedMoveWarning } from '../utils/stock-move-confirm.js';
import { reasonFromMovementKind, syncBlockReasonFromKind } from '../utils/movement-kind-reason.js';
import * as types from '../types.js';

function downloadBase64File(base64Content, filename, mimeType) {
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: mimeType });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename;
	a.click();
	URL.revokeObjectURL(a.href);
}

function downloadCsv(base64Content, filename) {
	downloadBase64File(base64Content, filename || 'relatorio.csv', 'text/csv;charset=utf-8');
}

const stocks = function()
{

	let stocks_crud = crud.create({
		default_columns: '1,2,12,13,15,16,17,18,19,20,21,23,24,26,25,28,27,29,30,3,4,5,6,7,8,9,37,38,39,10',
		insertRecord() {
			// Tela de estoque é somente consulta; criação/alteração é feita por entradas, movimentação e inventário
		},
		async run(module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, options = {}) {
			// Grade: alinhar colunas/checkboxes (v2=fabricante; v3=ordem/índices + coluna cód. cliente).
			try {
				const raw = localStorage.getItem('stocks.setup');
				if (raw) {
					const s = JSON.parse(raw);
					let touch = false;
					if (!s.stocks_grid_v2) {
						s.columns = this.default_columns;
						s.stocks_grid_v2 = true;
						touch = true;
					}
					if (!s.stocks_grid_v3) {
						s.columns = this.default_columns;
						s.stocks_grid_v3 = true;
						touch = true;
					}
					// Programa na grade = coluna exibida program_name (23.º campo do SELECT); índice 22 era program_id — desalinhava visibilidade (#MT-880B687).
				if (!s.stocks_grid_v4_program_col) {
					if (typeof s.columns === 'string' && /\b22\b/.test(s.columns)) {
						s.columns = s.columns.replace(/\b22\b/g, '23');
					}
					s.stocks_grid_v4_program_col = true;
					touch = true;
				}
				// v5: coluna Classes (33) adicionada — não inclusa no default; só reset se colunas ainda não existirem.
			if (!s.stocks_grid_v5_classes) {
				s.stocks_grid_v5_classes = true;
				touch = true;
			}
			if (!s.stocks_grid_v6_categoria) {
				s.stocks_grid_v6_categoria = true;
				touch = true;
			}
			if (!s.stocks_grid_v7_values) {
				s.columns = this.default_columns;
				s.stocks_grid_v7_values = true;
				touch = true;
			}
				if (touch)
						localStorage.setItem('stocks.setup', JSON.stringify(s));
				}
			} catch (_) { /* ignore */ }
			await Object.getPrototypeOf(this).run.call(this, module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, { ...options, skipInitialLoad: true });
			// Ocultar Inserir (tela somente consulta)
			const insertLi = this.target_node?.querySelector?.('li[hash="#insert"]');
			if (insertLi) insertLi.style.display = 'none';
			// #MT-FD0B172 — Configurar: colunas/ordenação já via applySetup.
			// "Preservar grade ao reabrir" (keep_filter=1) → recarrega grade SEM critérios do Filtrar.
			this.filter = {};
			const setup = this.loadSetup?.() || {};
			if (setup.filter != null || setup.filter_applied != null)
			{
				delete setup.filter;
				delete setup.filter_applied;
				try { localStorage.setItem('stocks.setup', JSON.stringify(setup)); } catch (_) { /* ignore */ }
			}
			if (setup.keep_filter === 1 || setup.keep_filter === true || setup.keep_filter === '1')
			{
				this.filterExplicitlyApplied = true;
				await this.loadPage(1);
				return;
			}
			const grid_node = this.target_node?.querySelector?.('#grid');
			if (grid_node) {
				grid_node.innerHTML = '<p class="mtm" style="color:var(--color-muted)">Clique em <strong>Filtrar</strong> e depois <strong>Aplicar</strong> para carregar os dados (ou aplicar filtros para refinar a busca).</p>';
			}
			const page = this.target_node?.querySelector?.('#page');
			if (page)
				page.innerText = '0/0';
		},
		async resolveFilterData(raw)
		{
			return await resolveFilterLabelsToIds(raw || this.filter || {}, this.fieldspecs);
		},
		normalizeFilterData(raw)
		{
			const out = { ...(raw || {}) };
			Object.keys(out).forEach((k) =>
			{
				const v = out[k];
				if (v === '-1' || v === -1 || v == null)
					out[k] = '';
			});
			return out;
		},
		async extractLocationScopeFilterRaw(raw)
		{
			const parts = [];
			const data = raw || {};
			const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
			let companies = null;
			const resolveCompanyId = async (v) =>
			{
				if (UUID_REGEX.test(v))
					return v;
				companies = companies ?? await db.list('companies').catch(() => []);
				const c = companies.find(x =>
					(x.nick || '') === v || String(x.nick || '') === v
					|| (x.name || '') === v || String(x.name || '') === v);
				const id = c?.id;
				return id && UUID_REGEX.test(String(id)) ? String(id) : null;
			};
			for (let i = 0; i < 10; i++)
			{
				const f = data[`field-${i}`];
				let v = data[`value-${i}`];
				if (v == null || v === '' || v === '-1' || v === -1)
					continue;
				v = String(v).trim();
				if (f === 'company_id' && v)
				{
					const cid = await resolveCompanyId(v);
					if (cid)
						parts.push(`company_id*eq*${cid}`);
				}
				else if (f === 'company_id')
					continue;
				else if (f === 'warehouse_id' && /^\d+$/.test(v))
					parts.push(`warehouse_id*eq*${v}`);
				else if (f === 'location_id' && v)
				{
					const m = v.match(/^(\d+):(\d+)$/);
					if (m)
					{
						parts.push(`warehouse_id*eq*${m[1]}`);
						parts.push(`location_id*eq*${m[2]}`);
					}
					else if (/^\d+$/.test(v))
						parts.push(`location_id*eq*${v}`);
				}
			}
			return parts.length ? parts.join(',') : null;
		},
		async loadScopedLocations(raw)
		{
			const scopeFilter = await this.extractLocationScopeFilterRaw(raw);
			if (!scopeFilter)
				return;
			const params = new URLSearchParams();
			params.append('filter', scopeFilter);
			params.append('sort', '4');
			params.append('limit', '0');
			const data = await api.call(api.list_locations, '?' + params.toString());
			await db.set('locations', Array.isArray(data) ? data : []);
			return data;
		},
		async loadBaseData()
		{
			const q = this.queryAll();
			await Promise.all([
			api.call(api.list_companies, q).then((data) => db.set('companies', data)).catch(error => notify(error)),
			api.call(api.list_warehouses, q).then((data) => db.set('warehouses', data)).catch(error => notify(error)),
			api.call(api.list_programs, q).then((data) => db.set('programs', data)).catch(error => notify(error)),
			api.call(api.list_devices, q).then((data) => db.set('devices', data)).catch(error => notify(error)),
			api.call(api.list_categories, q).then((data) => db.set('categories', data)).catch(error => notify(error))
			]);
			await this.loadScopedLocations(this.filter);
		},
		async queryData(query)
		{
			return await api.call(api.list_stocks, query);
		},
		async loadRecord(id)
		{
			const data = await api.call(api.select_stock, ...JSON.parse(id));
			if (data && typeof data === 'object') {
				// Mapear dados enriquecidos para os campos somente leitura do modal
				data.stock_sku = data.sku ?? '';
				data.stock_product_name = data.product_name ?? '';
				data.stock_ncm = data.ncm ?? '';
				data.stock_gtin = data.gtin ?? '';
				data.stock_packaging_code = data.packaging_code ?? '';
				data.stock_packaging_factor = data.packaging_factor ?? '';
				data.stock_packaging_qr = data.packaging_qr_code ?? '';
				data.stock_lot_number = formatLotNumber(data.lot_number) ?? '';
				data.stock_expiration_date = data.expiration_date ?? '';
				data.stock_manufacture_date = data.manufacture_date ?? '';
				data.stock_program_name = data.program_name ?? '';
				data.stock_codigo_cliente = data.codigo_cliente ?? '';
				data.stock_input_created_on = data.input_created_on ? (() => { const d = new Date(data.input_created_on); return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`; })() : '';
				data.stock_input_id = data.input_id ?? '';
				data.stock_invoice_number = data.invoice_number ?? '';
				data.stock_supplier_name = data.supplier_name ?? '';
				data.stock_supplier_legal_name = data.supplier_legal_name ?? '';
				data.stock_manufacturer_nick = data.manufacturer_nick ?? '';
				data.stock_block_reason = Number(data.quantity_unsettled || 0) > 0
					? String(data.block_reason || '').trim()
					: '';
			}
			return data;
		},
		async saveRecord() {
			// Tela de estoque é somente consulta; não persiste alterações por aqui
			return false;
		},
		async editFilter() {
			await this.loadScopedLocations({});
			filter.edit(this.applyFilter.bind(this), {}, {
				reloadLocations: async (formData) => {
					await this.loadScopedLocations(formData);
				}
			});
		},
		async applyFilter(data) {
			let resolved = await resolveFilterLabelsToIds(data, this.fieldspecs);
			resolved = this.normalizeFilterData(resolved);
			await this.loadScopedLocations(resolved);
			this.filter = resolved;
			this.filterExplicitlyApplied = true;
			await this.loadPage(1);
		},
		applySetup(data)
		{
			if (data.sort_column != null && data.sort_column !== '')
			{
				this.sort_column = Number(data.sort_column);
				this.sort_order = Number(data.sort_order ?? 1);
			}
			else
			{
				const rec = this.computeRecencySort();
				this.sort_column = rec.sort_column;
				this.sort_order = 1;
			}
			this.page_length = Number(data.page_length ?? this.page_length);
			this.filter = {};
			const _default = this.default_columns
				|| '1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25';
			const columns = (data.columns || _default).split(',').map((column) => Number(column));
			grid.setVisible(this.fieldspecs.map((field) => columns.includes(field.index) ? 1 : 0));
		},
		saveSetup(data)
		{
			const keepOn = data.keep_filter === true || data.keep_filter === 1 || data.keep_filter === '1';
			data.keep_filter = keepOn ? 1 : 0;
			delete data.filter;
			delete data.filter_applied;
			const prev = this.loadSetup() || {};
			for (const k of Object.keys(prev))
			{
				if ((k.startsWith('sm_') || k.startsWith('stocks_grid_')) && data[k] == null)
					data[k] = prev[k];
			}
			localStorage.setItem(this.module_name + '.setup', JSON.stringify(data));
			this.applySetup(data);
			if (keepOn)
			{
				this.filterExplicitlyApplied = true;
				this.loadPage();
			}
			else
			{
				this.filterExplicitlyApplied = false;
				const grid_node = this.target_node?.querySelector?.('#grid');
				if (grid_node)
					grid_node.innerHTML = '<p class="mtm" style="color:var(--color-muted)">Clique em <strong>Filtrar</strong> e depois <strong>Aplicar</strong> para carregar os dados (ou aplicar filtros para refinar a busca).</p>';
				const page = this.target_node?.querySelector?.('#page');
				if (page)
					page.innerText = '0/0';
			}
		},
		moveStock()
		{
			const showResult = async (success) =>
			{
				if (success)
				{
					this.loadPage();	// Show updated stocks
					fs.destroy();
				}
			}
			const sendRequest = async (data) =>
			{
				const reservedErr = manualMoveReservedError(data.source_stock, data.target_stock);
				if (reservedErr)
				{
					notify(reservedErr, 'Atenção');
					return;
				}
				let params = {
					'source_company_id': data.source_warehouse_company_id,
					'source_warehouse_id': Number(data.source_warehouse_id),
					'source_location_id': Number(data.source_location_id),
					'source_stock': data.source_stock,
					'target_company_id': data.target_warehouse_company_id,
					'target_warehouse_id': Number(data.target_warehouse_id),
					'target_location_id': Number(data.target_location_id),
					'target_stock': data.target_stock,
					'quantity': Number(data.quantity),
					'reason': data.reason,
					'output_id': Number(data.output_id)
				};
				const runMove = () =>
				{
					void api.call(api.move_stock, data.lot_company_id, data.lot_id, params).then(showResult);
				};
				if (data.source_stock === 'B')
				{
					ask(
						formatBlockedMoveWarning([{ sku: data.stock_sku, quantity: Number(data.quantity) }]),
						'Movimentar bloqueado',
						(key) =>
						{
							if (key !== 'Y')
								return;
							runMove();
						}
					);
					return;
				}
				runMove();
			}
			let fs = formset.create(move_stock_form, sendRequest);
			const crud = this;
			const origInit = fs.init.bind(fs);
			fs.init = async function()
			{
				origInit();
				// Preencher com estoque selecionado na grid
				const selected = await crud.getSelectedIds(['company_id', 'id']);
				if (selected?.length === 1)
				{
					const [company_id, stock_id] = selected[0];
					const stock = await api.call(api.select_stock, company_id, stock_id);
					if (stock)
					{
						fs.form.setValues({
							stock_sku: stock.sku ?? '',
							lot_company_id: stock.lot_company_id,
							lot_id: stock.lot_id,
							quantity: stock.quantity_available ?? 1,
							source_warehouse_company_id: stock.company_id,
							source_warehouse_id: stock.warehouse_id,
							source_location_id: stock.location_id
						});
						fs.form.refresh();
					}
				}
			};
			fs.init();
		},
		blockStock()
		{
			const showResult = async (success) =>
			{
				if (success)
				{
					this.loadPage();
					fs.destroy();
				}
			};
			const sendRequest = async (data) =>
			{
				const qty = Number(data.quantity);
				const maxL = Number(data.qty_available_max || 0);
				if (!Number.isFinite(qty) || qty < 1)
				{
					notify('Informe uma quantidade válida.', 'Atenção');
					return;
				}
				if (maxL > 0 && qty > maxL)
				{
					notify(`Quantidade não pode exceder o disponível (${maxL}).`, 'Atenção');
					return;
				}
				const mk = String(data.movement_kind || '').trim();
				if (!mk)
				{
					notify('Selecione a Natureza do bloqueio.', 'Atenção');
					return;
				}
				const reason = reasonFromMovementKind(mk);
				const params = {
					source_company_id: data.source_warehouse_company_id,
					source_warehouse_id: Number(data.source_warehouse_id),
					source_location_id: Number(data.source_location_id),
					source_stock: 'L',
					target_company_id: data.target_warehouse_company_id,
					target_warehouse_id: Number(data.target_warehouse_id),
					target_location_id: Number(data.target_location_id),
					target_stock: 'B',
					quantity: qty,
					reason,
					movement_kind: mk,
				};
				await api.call(api.move_stock, data.lot_company_id, data.lot_id, params).then(showResult);
			};
			const fs = formset.create(block_stock_form, sendRequest);
			const crud = this;
			const origInit = fs.init.bind(fs);
			fs.init = async function ()
			{
				const sel = crud.getSelection();
				if (sel.length !== 1)
				{
					notify('Selecione exatamente uma linha de estoque.', 'Atenção');
					return;
				}
				// Seleção da grade: data-id é JSON [company_id, stock_id] — não é objeto { company_id, id }
				const tuple = sel[0];
				const company_id = Array.isArray(tuple) ? tuple[0] : tuple?.company_id;
				const stock_id = Array.isArray(tuple) ? tuple[1] : tuple?.id;
				if (company_id == null || stock_id == null)
				{
					notify('Seleção inválida. Marque a caixa da linha e tente de novo.', 'Atenção');
					return;
				}
				const stock = await api.call(api.select_stock, company_id, stock_id);
				if (!stock)
				{
					notify('Estoque não encontrado.', 'Erro');
					return;
				}
				const q = Number(stock.quantity_available || 0);
				if (q < 1)
				{
					notify('Não há quantidade disponível (L) para bloquear nesta linha.', 'Atenção');
					return;
				}
				origInit();
				fs.form.setValues({
					stock_sku: stock.sku ?? '',
					lot_display: `${stock.lot_company_id} · lote ${stock.lot_id}`,
					location_display: `Arm. ${stock.warehouse_id} · pos. ${stock.location_id}`,
					movement_kind: 'BLOQ',
					reason: reasonFromMovementKind('BLOQ'),
					lot_company_id: stock.lot_company_id,
					lot_id: stock.lot_id,
					quantity: q,
					qty_available_max: q,
					source_warehouse_company_id: stock.company_id,
					source_warehouse_id: stock.warehouse_id,
					source_location_id: stock.location_id,
					target_warehouse_company_id: stock.company_id,
					target_warehouse_id: stock.warehouse_id,
					target_location_id: stock.location_id,
				});
				fs.form.refresh();
				syncBlockReasonFromKind(fs);
			};
			fs.init();
		},
		reportPayload(filterData, extra = {})
		{
			const filterExpr = filterData ? translate(filterData, null, this.fieldspecs) : '';
			const sortVal = this.sort_column * (this.sort_order ? -1 : 1);
			return { filter: filterExpr || undefined, sort: sortVal, ...extra };
		},
		openReportModal(title, apiMethod)
		{
			let reportFilter = null;
			const crud = this;
			const descriptor = { ...report_modal_form };
			descriptor.html = descriptor.html.replace('<div class="title" id="report-title">Relatório</div>', `<div class="title" id="report-title">${title}</div>`);
			const fs = formset.create(descriptor, async (data) =>
			{
				fs.destroy();
				const payload = crud.reportPayload(reportFilter, { format: data.format });
				try {
					const json = await api.call(apiMethod, payload);
					if (json?.pdf) pdf(json.pdf);
					else if (json?.csv) {
						downloadCsv(json.csv, json.filename || 'relatorio.csv');
						notify('Download iniciado.', 'Sucesso');
					}
					else notify('Nenhum dado para o relatório.', 'Aviso');
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			});
			fs.init = (() =>
			{
				Object.getPrototypeOf(fs).init.call(fs);
				const node = fs.dialog?.getNode?.();
				if (node) {
					const btnFilters = node.querySelector('#btn-filters');
					const summary = node.querySelector('#filter-summary');
					const updateSummary = () => {
						summary.textContent = reportFilter ? 'Filtros definidos' : 'Sem filtros (todos os registros)';
					};
					btnFilters?.addEventListener('click', async () =>
					{
						await crud.loadScopedLocations(reportFilter || crud.filter);
						filter.edit(async (data) =>
						{
							reportFilter = await resolveFilterLabelsToIds(data, crud.fieldspecs);
							await crud.loadScopedLocations(reportFilter);
							updateSummary();
						}, reportFilter, { reloadLocations: async (formData) =>
						{
							await crud.loadScopedLocations(formData);
						}});
					});
					updateSummary();
				}
			})();
		},
		printBySKU() { this.openReportModal('Estoque Consolidado por SKU', api.print_stocks_by_sku); },
		printByLot() { this.openReportModal('Estoque Consolidado por Lote', api.print_stocks_by_lot); },
		printByLocation() { this.openReportModal('Estoque Consolidado por Posição', api.print_stocks_by_location); },
		printBalanceBlocks() { this.openReportModal('Bloqueios de saldo', api.print_stocks_balance_blocks); },
		exportCsv()
		{
			const fs = formset.create(export_csv_form, async (data) =>
			{
				fs.destroy();
			const payload = {
				company_id: data.company_id,
				warehouse_id: data.warehouse_id ? Number(data.warehouse_id) : null,
				program_id: data.program_id ? Number(data.program_id) : null,
				class_name: data.class_name ? String(data.class_name).trim() : null,
			};
				try {
					const json = await api.call(api.export_stocks_csv, payload);
					if (json?.csv) {
						downloadCsv(json.csv, json.filename || 'estoque.csv');
						notify('Download iniciado.', 'Sucesso');
					} else {
						notify('Nenhum dado para exportar.', 'Aviso');
					}
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			});
			fs.init();
		},
		printDurability()
		{
			const fs = formset.create(print_durability_form, async (data) =>
			{
				if (!data.company_id) {
					notify('Selecione a empresa.', 'Atenção');
					return false;
				}
				const payload = {
					company_id: data.company_id,
					warehouse_id: data.warehouse_id ? Number(data.warehouse_id) : null,
					program_id: data.program_id ? Number(data.program_id) : null,
					only_negative: Boolean(data?.only_negative),
				};
				try {
					const json = await api.call(api.print_stock_durability, payload);
					if (json?.xlsx) {
						downloadBase64File(
							json.xlsx,
							json.filename || 'durabilidade-estoque.xlsx',
							'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
						);
						notify('Download do Excel iniciado.', 'Sucesso');
						fs.destroy();
					} else {
						notify('Nenhum dado para exportar.', 'Aviso');
					}
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
				return false;
			});
			fs.init();
		},
		skuNoOutput()
		{
			const fs = formset.create(sku_no_output_form, async (data) =>
			{
				const companyId = data.company_id;
				if (!companyId) {
					notify('Selecione a empresa.', 'Atenção');
					return false;
				}
				const dateFrom = data.date_from;
				const dateTo   = data.date_to;
				if (!dateFrom || !dateTo) {
					notify('Informe o período (De e Até).', 'Atenção');
					return false;
				}
				if (dateTo < dateFrom) {
					notify('A data "Até" deve ser igual ou posterior à data "De".', 'Atenção');
					return false;
				}
				fs.destroy();
				try {
					// Busca em tela (JSON) e exibe tabela em modal; também oferece CSV.
					const respJson = await api.sku_no_output_report(companyId, {
						format: 'json',
						date_from: dateFrom,
						date_to:   dateTo,
						warehouse_id: data.warehouse_id || undefined,
						limit: 500,
						offset: 0,
					});
					if (!respJson.ok) {
						const err = await respJson.json().catch(() => ({}));
						notify(err?.error || 'Erro ao consultar.', 'Erro');
						return;
					}
					const rows = await respJson.json();
					if (!rows || rows.length === 0) {
						notify('Nenhum produto sem saída neste período e filtros.', 'Resultado');
						return;
					}
					const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (ch) => (
						{ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
					));
					const fmtCusto = (v) => {
						if (v == null || v === '') return '—';
						const n = Number(v);
						if (!Number.isFinite(n)) return esc(v);
						return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
					};
					const tbody = rows.map(r => `
						<tr>
							<td>${esc(r.sku) || '—'}</td>
							<td>${esc(r.produto) || '—'}</td>
							<td>${esc(r.unidade) || '—'}</td>
							<td>${esc(r.programa) || '—'}</td>
							<td>${esc(r.lote) || '—'}</td>
							<td class="tac">${esc(r.validade) || '—'}</td>
							<td class="tar">${r.estoque_total ?? '—'}</td>
							<td class="tar">${fmtCusto(r.custo_unitario)}</td>
							<td class="tac">${esc(r.ultima_saida) || 'Nunca'}</td>
						</tr>`).join('');
					const total = rows.length;
					const avisoMais = total >= 500
						? `<p class="fss" style="color:var(--color-muted)">Exibindo os primeiros 500 registros. Exporte o CSV para ver todos.</p>`
						: '';
					const html = `
						<div class="msgbox" style="max-width:90vw;overflow:auto">
							<div class="mb-title">SKUs sem Saída — ${esc(dateFrom)} a ${esc(dateTo)} (${total} linha${total !== 1 ? 's' : ''})</div>
							${avisoMais}
							<div style="max-height:60vh;overflow:auto">
								<table class="fsx bp" style="min-width:960px">
									<thead><tr>
										<th>SKU</th><th>Produto</th><th>Unidade</th>
										<th>Programa</th><th>Lote</th><th class="tac">Validade</th>
										<th class="tar">Estoque</th><th class="tar">Custo unit.</th>
										<th class="tac">Última Saída</th>
									</tr></thead>
									<tbody>${tbody}</tbody>
								</table>
							</div>
							<div class="fsb mtm">
								<button type="button" class="button" id="btn-snor-csv">Exportar CSV</button>
								<button type="button" class="button bn_N">Fechar</button>
							</div>
						</div>`;
					const { default: dialog } = await import('../../versa/dialog.js');
					const dlg = dialog.create(html, (key) => {
						if (key === 'N' || key === 'Escape') dlg.destroy();
					}, /N|Escape/);
					// Botão exportar CSV
					dlg.getNode().querySelector('#btn-snor-csv')?.addEventListener('click', async () => {
						try {
							const respCsv = await api.sku_no_output_report(companyId, {
								format: 'csv',
								date_from: dateFrom,
								date_to:   dateTo,
								warehouse_id: data.warehouse_id || undefined,
							});
							if (!respCsv.ok) {
								const err2 = await respCsv.json().catch(() => ({}));
								notify(err2?.error || 'Erro ao gerar CSV.', 'Erro');
								return;
							}
							const result = await respCsv.json();
							if (result?.csv) {
								downloadCsv(result.csv, result.filename || 'skus-sem-saida.csv');
								notify('Download iniciado.', 'Sucesso');
							} else {
								notify('Nenhum dado para exportar.', 'Aviso');
							}
						} catch (e) {
							notify(e?.message || String(e), 'Erro');
						}
					});
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			});
			fs.init();
		},

		outputPurchaseReport()
		{
			const fs = formset.create(output_purchase_form, async (data) =>
			{
				const companyId = data.company_id;
				if (!companyId) {
					notify('Selecione a empresa.', 'Atenção');
					return false;
				}
				const dateFrom = data.date_from;
				const dateTo   = data.date_to;
				if (!dateFrom || !dateTo) {
					notify('Informe o período (De e Até).', 'Atenção');
					return false;
				}
				if (dateTo < dateFrom) {
					notify('A data "Até" deve ser igual ou posterior à data "De".', 'Atenção');
					return false;
				}
				fs.destroy();
				try {
					const resp = await api.output_purchase_report(companyId, {
						date_from: dateFrom,
						date_to:   dateTo,
						warehouse_id: data.warehouse_id || undefined,
					});
					if (!resp.ok) {
						const err = await resp.json().catch(() => ({}));
						notify(err?.error || 'Erro ao exportar.', 'Erro');
						return;
					}
					const json = await resp.json();
					if (json?.csv) {
						downloadCsv(json.csv, json.filename || 'saidas-compras.csv');
						notify(`Download iniciado (${json.rows ?? ''} linha(s)).`, 'Sucesso');
					} else {
						notify('Nenhum dado para exportar.', 'Aviso');
					}
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			});
			fs.init();
		},

		printLotLabels()
		{
			if (!this.ensureSelection('Etiquetas de Lote'))
				return;
			const sendRequest = async (data) =>
			{
				const device_id = resolvePrinterDeviceId(data.device_id, '#stocks-select-printer #device_id');
				if (device_id == null)
				{
					notify('Selecione a impressora na lista (clique na opção; só o texto no campo não basta).', 'Atenção');
					return false;
				}
				const selected = await this.getSelectedIds(['company_id', 'id']);
				if (!selected.length)
				{
					notify('Selecione ao menos um estoque na grade antes de continuar.', 'Etiquetas de Lote');
					return false;
				}
				const payload = {
					company_id: data.company_id,
					device_id,
					ids: splitIdsByCompany(selected),
					return_zpl: !!data.use_local_agent,
				};
				let lc = parseInt(String(data.label_copies ?? '1'), 10);
				if (!Number.isFinite(lc) || lc < 1) lc = 1;
				if (lc > 200) lc = 200;
				payload.label_copies = lc;
				const result = await api.call(api.print_lot_labels, payload);
				if (result?.zpl) {
					try {
						await api.sendZplToAgent(result.zpl, result.printer, result.printer_key);
						notify('Etiquetas enviadas ao agente local.', 'Impressão');
					} catch (e) {
						notify(e?.message || String(e), 'Agente local');
						return false;
					}
				}
				return result;
			}
			let fs = formset.create(select_printer_form, sendRequest);
			fs.init = (() =>
			{
				document.querySelector(`${fs.form.selector} #count`).innerHTML = this.getCount();
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
	});
	
	// Ordem = colunas da grade (alinhado a js/views/stocks.js). Índice = posição 1-based no SELECT SQL_LIST_BY_CREDENTIAL_ENRICHED (src/repos/stocks.py); «Programa» = coluna 23 (program_name), não 22 (program_id). Coluna 32 extra: `warehouse_name` (só API / introspect; não usada nos fieldspecs da grade).
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Estoque', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador do estoque' },
		{ index: 12, field: 'sku', label: 'SKU', types: [types.name()], placeholder: '', title: 'SKU do produto' },
		{ index: 13, field: 'product_name', label: 'Produto', types: [types.name()], placeholder: '', title: 'Nome/taxonomia do produto' },
		{ index: 15, field: 'manufacturer_nick', label: 'Fabricante', types: [types.name()], placeholder: '', title: 'Com embalagem no lote: fabricante GTIN da embalagem; sem embalagem cadastrada: fabricante legado do produto' },
		{ index: 16, field: 'ncm', label: 'NCM', types: [types.name()], placeholder: '', title: 'NCM do produto' },
		{ index: 17, field: 'gtin', label: 'EAN', types: [types.name()], placeholder: '', title: 'GTIN/EAN da embalagem do lote (sem GTIN na embalagem: cadastro do produto)' },
		{ index: 18, field: 'codigo_cliente', label: 'Cód. cliente', types: [types.name()], placeholder: '', title: 'Código do cliente no produto' },
		{ index: 19, field: 'packaging_code', label: 'Embalagem', types: [types.name()], placeholder: '', title: 'Código da embalagem padrão do produto' },
		{ index: 20, field: 'packaging_factor', label: 'Fator Emb.', types: [types.number(0, 2)], placeholder: '', title: 'Fator da embalagem padrão' },
		{ index: 21, field: 'packaging_qr_code', label: 'QR Emb.', types: [types.name()], placeholder: '', title: 'QR code da embalagem padrão' },
		{ index: 23, field: 'program_id', label: 'Programa', types: [types.datalist()], placeholder: '', title: 'Programa do lote', store: 'programs', labels: ['name'] },
		{ index: 24, field: 'lot_number', label: 'Lote fab.', types: [types.name()], placeholder: '', title: 'Número do lote fabricante' },
		{ index: 26, field: 'expiration_date', label: 'Validade', types: [types.date()], placeholder: '', title: 'Data de validade do lote' },
		{ index: 25, field: 'manufacture_date', label: 'Fabricação', types: [types.date()], placeholder: '', title: 'Data de fabricação do lote' },
		{ index: 28, field: 'input_created_on', label: 'Data Entrada', types: [types.date()], placeholder: '', title: 'Data da entrada referente ao lote' },
		{ index: 27, field: 'input_id', label: 'Entrada Sial', types: [types.number(0, 2)], placeholder: '', title: 'Número da entrada SIAL referente ao lote' },
		{ index: 29, field: 'invoice_number', label: 'Nota Entrada', types: [types.name()], placeholder: '', title: 'Número da nota de entrada' },
		{ index: 30, field: 'supplier_name', label: 'Fornecedor', types: [types.name()], placeholder: '', title: 'Apelido do fornecedor da entrada' },
		{ index: 39, field: 'supplier_legal_name', label: 'Fornecedor (nome)', types: [types.name()], placeholder: '', title: 'Nome / razão social do fornecedor da entrada' },
		{ index: 3, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém do estoque', store: 'warehouses', labels: ['name'] },
		{ index: 4, field: 'location_id', label: 'Posição', types: [types.datalist()], placeholder: '', title: 'Posição no armazém', store: 'locations', labels: ['address'], separator: '-' },
		{ index: 5, field: 'lot_company_id', label: 'Titular', types: [types.datalist()], placeholder: '', title: 'Titular do lote', store: 'companies', labels: ['nick'] },
		{ index: 6, field: 'lot_id', label: 'Lote', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador do lote' },
		{ index: 7, field: 'quantity_available', label: 'Disponível', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade disponível' },
		{ index: 8, field: 'quantity_reserved', label: 'Reservado', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade reservada' },
		{ index: 9, field: 'quantity_unsettled', label: 'Bloqueado', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade sob análise' },
	{ index: 37, field: 'unit_price', label: 'Valor unit.', types: [types.number(4, 2)], placeholder: '0,0000', title: 'Valor unitário médio do item no pedido de entrada' },
	{ index: 38, field: 'total_value', label: 'Valor total', types: [types.number(2, 2)], placeholder: '0,00', title: 'Valor unitário x quantidade disponível' },
	{ index: 10, field: 'status', label: 'Status', types: [types.opt_stock_status(), types.datalist()], placeholder: '', title: 'Status do estoque' },
	{ index: 33, field: 'classes', label: 'Classes', types: [types.name()], placeholder: '', title: 'Classes do produto (separadas por ;)' },
	{ index: 34, field: 'categoria', label: 'Categoria', types: [types.name(), types.datalist()], placeholder: '', title: 'Categoria do produto', store: 'categories', labels: ['name'] },
	{ index: 35, field: 'is_portaria_344', label: 'Portaria 344', types: [types.opt_bool()], placeholder: '', title: 'Produto sujeito à Portaria 344/98' },
];

	let operations = [
		{ hash: '#move-stock', label: 'Mover&nbsp;estoque' },
		{ hash: '#block-stock', label: 'Bloquear&nbsp;estoque' },
	];

	let reports = [
		{ hash: '#print-by-sku',		label: 'Estoque&nbsp;Consolidado&nbsp;por&nbsp;SKU' },
		{ hash: '#print-by-lot',		label: 'Estoque&nbsp;Consolidado&nbsp;por&nbsp;Lote' },
		{ hash: '#print-by-location',	label: 'Estoque&nbsp;Consolidado&nbsp;por&nbsp;Posição' },
		{ hash: '#print-balance-blocks',	label: 'Bloqueios&nbsp;de&nbsp;saldo' },
		{ hash: '#export-csv',			label: 'Exportar&nbsp;CSV&nbsp;(Relatório&nbsp;Completo)' },
		{ hash: '#print-durability',	label: 'Durabilidade&nbsp;de&nbsp;Estoque&nbsp;(Excel)' },
		{ hash: '#lot-labels',			label: 'Etiquetas&nbsp;de&nbsp;Lotes' },
		{ hash: '#sku-no-output',		label: 'SKUs&nbsp;sem&nbsp;Saída&nbsp;(Período)' },
		{ hash: '#output-purchase',		label: 'Saídas&nbsp;para&nbsp;Compras&nbsp;(CSV)' },
	];

	router.set('#move-stock', 			stocks_crud.moveStock.bind(stocks_crud));
	router.set('#block-stock',			stocks_crud.blockStock.bind(stocks_crud));
	router.set('#print-by-sku',			stocks_crud.printBySKU.bind(stocks_crud));
	router.set('#print-by-lot',			stocks_crud.printByLot.bind(stocks_crud));
	router.set('#print-by-location',	stocks_crud.printByLocation.bind(stocks_crud));
	router.set('#print-balance-blocks',	stocks_crud.printBalanceBlocks.bind(stocks_crud));
	router.set('#export-csv',			stocks_crud.exportCsv.bind(stocks_crud));
	router.set('#print-durability',		stocks_crud.printDurability.bind(stocks_crud));
	router.set('#lot-labels',			stocks_crud.printLotLabels.bind(stocks_crud));
	router.set('#sku-no-output',		stocks_crud.skuNoOutput.bind(stocks_crud));
	router.set('#output-purchase',		stocks_crud.outputPurchaseReport.bind(stocks_crud));

	stocks_crud.run(
		'stocks',
		'Estoques',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports
	);

};

export default stocks;

